import React, { useState, useEffect } from 'react';

/**
 * Reusable circular avatar with an `onError` fallback to name-initials.
 *
 * - Renders the image when `src` is a non-empty string AND it loads successfully
 * - Falls back to initials (or a "?" if no name) on any of:
 *     • missing/empty src
 *     • image fetch fails (broken URL, 404, CORS)
 * - Keeps a stable size via inline width/height so it never jumps
 * - Extra styling (rings, borders, custom bg) is merged via `className`
 */
function initialsOf(name) {
  if (!name) return '?';
  const parts = String(name).trim().split(/\s+/);
  if (parts.length === 0) return '?';
  return parts.map((w) => w[0]).join('').toUpperCase().slice(0, 2);
}

// Rough size → font-size mapping so initials scale with the avatar
function fontSizeFor(size) {
  if (size <= 24) return 9;
  if (size <= 32) return 10;
  if (size <= 40) return 11;
  if (size <= 56) return 14;
  if (size <= 80) return 18;
  return 22;
}

export default function Avatar({
  name,
  src,
  size = 40,
  className = '',
  alt = '',
  testId,
}) {
  const [broken, setBroken] = useState(!src);

  // Reset the broken flag whenever the src changes (e.g. after a successful upload)
  useEffect(() => { setBroken(!src); }, [src]);

  const dim = { width: size, height: size };
  const showImage = !!src && !broken;

  if (showImage) {
    return (
      <img
        src={src}
        alt={alt}
        onError={() => setBroken(true)}
        style={dim}
        className={`rounded-full object-cover flex-shrink-0 ${className}`}
        data-testid={testId}
      />
    );
  }

  return (
    <div
      role="img"
      aria-label={name || 'User'}
      style={{ ...dim, fontSize: fontSizeFor(size) }}
      className={`rounded-full bg-[#EFF2F7] text-[#0B4F3F] font-bold flex items-center justify-center flex-shrink-0 select-none ${className}`}
      data-testid={testId}
    >
      {initialsOf(name)}
    </div>
  );
}
